import './home.css';
import { initNav } from '../components/nav.js';
import { initAuthUI } from '../auth/auth-ui.js';
import { buildDocClient, fetchIdentityId } from '../core/aws.js';
import { listRecipes, virtualRecipeForIngredient } from '../model/recipes.js';
import { listIngredients, formatEnergy, ENERGY_UNITS } from '../model/ingredients.js';
import {
  getMenu,
  listMenus,
  listArchivedMenus,
  dateForMenuId,
  formatLocalDate,
  OTHER_SLOT,
} from '../model/menu.js';
import { menuStreaks, MIN_STREAK_DAYS } from '../model/streaks.js';
import { getSurplusKj } from '../model/surplus.js';
import { listListItems } from '../model/shopping.js';
import { getEnergyUnit } from '../model/settings.js';
import { createTagEl } from '../components/tags.js';

// The home page is a launcher: a grid of cards linking to each of the app's
// pages (static markup in index.html). Once signed in it ALSO shows a small set
// of personalised at-a-glance tiles at the top (surplus energy, today's menu,
// shopping-list count). Each tile is read-only and acts as a single link to the
// relevant page; a tile with no content is omitted entirely.
async function main() {
  initNav();
  await initAuthUI(handleSignedIn);
}

main().catch((err) => console.error('Page bootstrap failed:', err));

async function handleSignedIn(session) {
  const { idToken } = session;
  const doc = await buildDocClient(idToken);
  const userId = await fetchIdentityId(idToken);

  const today = formatLocalDate(new Date());

  const [energyUnit, surplusKj, todayMenu, allMenus, recipes, ingredients, listItems, archives] =
    await Promise.all([
      getEnergyUnit(doc, userId),
      getSurplusKj(doc, userId),
      getMenu(doc, userId, today),
      listMenus(doc, userId),
      listRecipes(doc, userId),
      listIngredients(doc),
      listListItems(doc),
      listArchivedMenus(doc, userId),
    ]);

  // Days that are still on the menu but dated before today need finishing — a
  // finished day is deleted, so any lingering past-dated menu (even an empty one)
  // is awaiting a "finish".
  const oldMenuCount = allMenus.filter((m) => dateForMenuId(m.itemId) < today).length;

  // Live menu days up to today are still awaiting "finish" — graced by streaks.
  const pendingDates = allMenus.map((m) => dateForMenuId(m.itemId)).filter((d) => d <= today);
  const streaks = menuStreaks(archives, { today, pendingDates });

  // Build the recipe-resolution map (stored recipes + a virtual single-serve
  // recipe per ingredient) so we can resolve any saved menu entry's display
  // name, including `recipe#ingredient:<id>` refs — mirrors the menu page.
  const recipesById = new Map(recipes.map((r) => [r.itemId, r]));
  for (const ing of ingredients) {
    const virtual = virtualRecipeForIngredient(ing);
    recipesById.set(virtual.itemId, virtual);
  }

  const tiles = buildTiles({
    energyUnit,
    surplusKj,
    todayMenu,
    recipesById,
    listItemCount: listItems.length,
    oldMenuCount,
    streaks,
  });

  renderTiles(tiles);
}

/** The user's display unit label ('kJ' | 'kcal'). */
function unitLabel(energyUnit) {
  return energyUnit === ENERGY_UNITS.KJ ? 'kJ' : 'kcal';
}

/** Format a stored kJ value for display in the user's unit, with separators. */
function displayEnergy(kj, energyUnit) {
  return (formatEnergy(kj, energyUnit) ?? 0).toLocaleString();
}

/**
 * Assemble the tile descriptors for the tiles that have content. Pure aside from
 * reading its inputs; returns an array of tile descriptors, each with an `href`,
 * a labelled head, and either a `value`/`detail` line or a custom `content`
 * node (the menu tile lists its items grouped by meal).
 */
function buildTiles({
  energyUnit,
  surplusKj,
  todayMenu,
  recipesById,
  listItemCount,
  oldMenuCount,
  streaks,
}) {
  const tiles = [];
  const unit = unitLabel(energyUnit);

  // 1. Old menus — past-dated days still awaiting a "finish". Only when any exist.
  if (oldMenuCount > 0) {
    tiles.push({
      icon: 'fa-calendar-xmark',
      label: 'Days to finish',
      value: `${oldMenuCount} ${oldMenuCount === 1 ? 'day' : 'days'}`,
      detail: 'Older menu days need finishing',
      href: '/menu',
    });
  }

  // 2. Shopping list — only when there's at least one item on it.
  if (listItemCount > 0) {
    tiles.push({
      icon: 'fa-basket-shopping',
      label: 'Shopping list',
      value: `${listItemCount} ${listItemCount === 1 ? 'item' : 'items'}`,
      href: '/shopping',
    });
  }

  // 3. Surplus energy — only when there's a positive banked balance.
  if (surplusKj > 0) {
    tiles.push({
      icon: 'fa-battery-half',
      label: 'Surplus energy',
      value: `${displayEnergy(surplusKj, energyUnit)} ${unit}`,
      href: '/menu',
    });
  }

  // 4. Menu streaks — each streak listed only while it's at least
  // MIN_STREAK_DAYS long; the tile is omitted when neither qualifies.
  const streakContent = renderStreakContent(streaks);
  if (streakContent) {
    tiles.push({
      icon: 'fa-fire',
      label: 'Menu streaks',
      content: streakContent,
      href: '/archive',
    });
  }

  // 5. Today's menu (last, so it fills the right column) — only when a menu with
  // entries exists for today. Lists the meals grouped by slot (tag label), with
  // what's left to eat and what's already been eaten.
  const slots = groupMenuBySlot(todayMenu, recipesById);
  if (slots.length > 0) {
    tiles.push({
      icon: 'fa-calendar-day',
      label: "Today's menu",
      content: renderMenuContent(slots),
      href: '/menu',
    });
  }

  return tiles;
}

/**
 * Group today's menu entries by slot (meal tag), and within each slot merge
 * identical recipes, summing servings + eaten. Returns
 * `[{ slot, items:[{ name, remaining, eaten }] }]` in first-seen slot order,
 * skipping any that resolve to nothing. Mirrors the menu page's grouping.
 */
function groupMenuBySlot(menu, recipesById) {
  const bySlot = new Map(); // slot -> Map(recipeRef -> { name, remaining, eaten })
  for (const entry of menu?.entries ?? []) {
    const slot = entry.slot ?? OTHER_SLOT;
    const total = Math.max(0, Number(entry.servings) || 0);
    if (total === 0) continue;
    const eaten = Math.max(0, Math.min(total, Number(entry.eaten) || 0));
    const name = recipesById.get(entry.recipeRef)?.name ?? 'This meal';

    if (!bySlot.has(slot)) bySlot.set(slot, new Map());
    const items = bySlot.get(slot);
    const existing = items.get(entry.recipeRef);
    if (existing) {
      existing.remaining += total - eaten;
      existing.eaten += eaten;
    } else {
      items.set(entry.recipeRef, { name, remaining: total - eaten, eaten });
    }
  }

  return [...bySlot.entries()].map(([slot, items]) => ({
    slot,
    items: [...items.values()],
  }));
}

const STREAK_LINES = [
  { key: 'onTarget', text: 'on target' },
  { key: 'tracked', text: 'tracked' },
];

const days = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;

/**
 * The streak tile body: one value + "Best" detail per qualifying streak, or
 * null when no streak is at least MIN_STREAK_DAYS long.
 */
function renderStreakContent(streaks) {
  const lines = STREAK_LINES.filter(({ key }) => streaks?.[key]?.current >= MIN_STREAK_DAYS);
  if (lines.length === 0) return null;

  const wrap = document.createElement('span');
  wrap.className = 'home-tile__streaks';
  for (const { key, text } of lines) {
    const { current, best } = streaks[key];
    const item = document.createElement('span');
    item.className = 'home-tile__streak';
    item.dataset.streak = key;
    const value = document.createElement('span');
    value.className = 'home-tile__value';
    value.textContent = `${days(current)} ${text}`;
    const detail = document.createElement('span');
    detail.className = 'home-tile__detail';
    detail.textContent = `Best: ${days(best)}${current >= best ? ' (this one!)' : ''}`;
    item.append(value, detail);
    wrap.appendChild(item);
  }
  return wrap;
}

/** Build the today's-menu tile body: each slot's tag label + its item lines. */
function renderMenuContent(slots) {
  const wrap = document.createElement('span');
  wrap.className = 'home-tile__menu';

  for (const { slot, items } of slots) {
    const group = document.createElement('span');
    group.className = 'home-tile__slot';

    const badge = createTagEl(slot);
    badge.classList.add('home-tile__slot-tag');
    group.appendChild(badge);

    for (const item of items) {
      group.appendChild(renderMenuItem(item));
    }
    wrap.appendChild(group);
  }

  return wrap;
}

/** One meal line: name + serving count, struck through when fully eaten. A
 * partially-eaten meal shows its remaining count and notes how many are eaten. */
function renderMenuItem({ name, remaining, eaten }) {
  const line = document.createElement('span');
  line.className = 'home-tile__meal';
  const fullyEaten = remaining === 0 && eaten > 0;
  if (fullyEaten) line.classList.add('home-tile__meal--eaten');

  // Show the count that matters: servings left to eat, or (when all eaten) how
  // many were eaten.
  const count = fullyEaten ? eaten : remaining;
  const nameEl = document.createElement('span');
  nameEl.className = 'home-tile__meal-name';
  nameEl.textContent = count > 1 ? `${name} ×${count}` : name;
  line.appendChild(nameEl);

  // Note partially-eaten meals so the remaining count reads correctly.
  if (eaten > 0 && remaining > 0) {
    const note = document.createElement('span');
    note.className = 'home-tile__meal-note';
    note.textContent = `(${eaten} eaten)`;
    line.appendChild(note);
  }

  return line;
}

/** Build one tile element: a single link acting as a read-only info button. */
function createTile({ icon, label, value, detail, content, href }) {
  const tile = document.createElement('a');
  tile.className = 'home-tile';
  tile.href = href;

  const head = document.createElement('span');
  head.className = 'home-tile__head';
  const iconEl = document.createElement('i');
  iconEl.className = `fa-solid ${icon}`;
  iconEl.setAttribute('aria-hidden', 'true');
  const labelEl = document.createElement('span');
  labelEl.className = 'home-tile__label';
  labelEl.textContent = label;
  head.append(iconEl, labelEl);
  tile.appendChild(head);

  // A tile is either a prominent single value (+ optional detail) or a custom
  // content node (the menu tile's grouped item list).
  if (content) {
    tile.appendChild(content);
  } else {
    const valueEl = document.createElement('span');
    valueEl.className = 'home-tile__value';
    valueEl.textContent = value;
    tile.appendChild(valueEl);

    if (detail) {
      const detailEl = document.createElement('span');
      detailEl.className = 'home-tile__detail';
      detailEl.textContent = detail;
      tile.appendChild(detailEl);
    }
  }

  return tile;
}

/** Render the tiles into the grid, revealing the section only if any exist. */
function renderTiles(tiles) {
  const grid = document.getElementById('home-tiles-grid');
  const section = document.getElementById('home-tiles');
  if (!grid || !section) return;

  grid.replaceChildren(...tiles.map(createTile));
  // Hide the whole Dashboard section (heading included) when no tile applies, so
  // we never show an empty section. It also carries `requires-auth`, so it's
  // only ever visible once signed in.
  section.hidden = tiles.length === 0;
}
